import React from "react";
import { COMPARISONS, getComparison } from "./compare/competitors";
import { Sprout, ArrowRight, ArrowLeft, Check, Minus, GitCompareArrows } from "lucide-react";
import { useSEO } from "../lib/useSEO";

function Nav({ onHome, onGetStarted }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-forest-900/85 border-b border-white/10">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <button data-testid="compare-home" onClick={onHome} className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-5 w-5" strokeWidth={2.4} /></div>
          <span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
        </button>
        <div className="flex items-center gap-4">
          <a href="/tools" className="text-sm font-semibold text-white/70 hover:text-white">Free tools</a>
          <a href="/blog" className="text-sm font-semibold text-white/70 hover:text-white">Resources</a>
          <button data-testid="compare-nav-cta" onClick={onGetStarted} className="group flex items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-sm font-bold hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
            Start free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </header>
  );
}

function ComparePage({ cmp, onHome, onGetStarted }) {
  useSEO({
    title: `${cmp.title} | AssortIQ`,
    description: cmp.description,
    canonical: `https://getassortiq.com/compare/${cmp.slug}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: `What is a good ${cmp.competitor} alternative?`,
          acceptedAnswer: { "@type": "Answer", text: cmp.description } },
        { "@type": "Question", name: `Who is AssortIQ best for?`,
          acceptedAnswer: { "@type": "Answer", text: cmp.bestFor } },
      ],
    },
  });
  const cell = (val) => {
    if (val === "—") return <span className="inline-flex items-center gap-1 text-white/40"><Minus className="h-4 w-4" /></span>;
    return val;
  };
  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased">
      <Nav onHome={onHome} onGetStarted={onGetStarted} />
      <div className="mx-auto max-w-4xl px-6 py-12">
        <a href="/compare" data-testid="compare-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> All comparisons</a>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider"><GitCompareArrows className="h-3.5 w-3.5" /> AssortIQ vs {cmp.competitor}</div>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">{cmp.h1}</h1>
        <p className="mt-4 text-white/70 leading-relaxed">{cmp.lead}</p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-white/50">Where {cmp.competitor} fits</div>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">{cmp.theirStrength}</p>
          </div>
          <div className="rounded-2xl border border-ai/25 bg-ai/[0.05] p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-ai">Where AssortIQ fits</div>
            <p className="mt-2 text-sm text-white/85 leading-relaxed">{cmp.bestFor}</p>
          </div>
        </div>

        <h2 className="mt-12 font-heading text-2xl font-extrabold">AssortIQ vs {cmp.competitor}, side by side</h2>
        <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.04] text-left">
                <th className="py-3 px-4 font-semibold text-white/60"> </th>
                <th className="py-3 px-4 font-semibold text-white/60">{cmp.competitor}</th>
                <th className="py-3 px-4 font-bold text-ai">AssortIQ</th>
              </tr>
            </thead>
            <tbody>
              {cmp.rows.map(([label, them, us]) => (
                <tr key={label} className="border-t border-white/8">
                  <td className="py-3 px-4 font-medium text-white/80">{label}</td>
                  <td className="py-3 px-4 text-white/60">{cell(them)}</td>
                  <td className="py-3 px-4 text-white/90"><span className="inline-flex items-start gap-1.5"><Check className="h-4 w-4 text-ai mt-0.5 shrink-0" />{us}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-12 font-heading text-2xl font-extrabold">Why operators switch to AssortIQ</h2>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          {cmp.reasons.map((r) => (
            <div key={r.h} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="font-heading font-bold text-white">{r.h}</div>
              <p className="mt-2 text-sm text-white/65 leading-relaxed">{r.p}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 relative overflow-hidden rounded-3xl border border-ai/30 bg-gradient-to-br from-ai/20 to-transparent p-8 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,94,0,0.15),transparent_70%)]" />
          <div className="relative">
            <h3 className="font-heading text-2xl font-extrabold">See AssortIQ on your own data</h3>
            <p className="mt-2 text-white/70">No six-month rollout. Import a CSV or connect your POS and get explainable recommendations in the first session — free for 7 days.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button data-testid="compare-cta" onClick={onGetStarted} className="inline-flex items-center gap-2 rounded-full bg-ai px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">Start free trial <ArrowRight className="h-4 w-4" /></button>
              <a href="/demo/grocery" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors">Try a live demo</a>
            </div>
          </div>
        </div>
      </div>
      <footer className="border-t border-white/10"><div className="mx-auto max-w-4xl px-6 py-8 text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Comparisons reflect our understanding of publicly available information; verify current capabilities with each vendor.</div></footer>
    </div>
  );
}

function Index({ onHome, onGetStarted }) {
  useSEO({
    title: "AssortIQ vs RELEX, Toolio & LEAFIO — Assortment Software Comparisons",
    description: "Compare AssortIQ to RELEX, Toolio and LEAFIO. An explainable, fast-to-deploy assortment optimization alternative for independents, regionals and agriculture.",
    canonical: "https://getassortiq.com/compare",
  });
  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased">
      <Nav onHome={onHome} onGetStarted={onGetStarted} />
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider"><GitCompareArrows className="h-3.5 w-3.5" /> Comparisons</div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold">How AssortIQ compares</h1>
          <p className="mt-3 text-white/60">Honest, side-by-side comparisons with the platforms operators evaluate most — so you can find the right fit for your size and industry.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="compare-grid">
          {COMPARISONS.map((c) => (
            <a key={c.slug} href={`/compare/${c.slug}`} data-testid={`compare-card-${c.slug}`} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-ai/40 hover:-translate-y-1 transition-all">
              <div className="text-xs font-bold uppercase tracking-wider text-ai">AssortIQ vs {c.competitor}</div>
              <h2 className="mt-2 font-heading text-lg font-bold leading-snug group-hover:text-ai transition-colors">{c.h1}</h2>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{c.bestFor.slice(0, 110)}…</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-ai group-hover:gap-2.5 transition-all">See comparison <ArrowRight className="h-4 w-4" /></span>
            </a>
          ))}
        </div>
      </div>
      <footer className="border-t border-white/10"><div className="mx-auto max-w-7xl px-6 py-8 text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Built for grocery, retail & agriculture.</div></footer>
    </div>
  );
}

export default function Compare({ slug, onHome, onGetStarted }) {
  const cmp = slug ? getComparison(slug) : null;
  if (slug && cmp) return <ComparePage cmp={cmp} onHome={onHome} onGetStarted={onGetStarted} />;
  return <Index onHome={onHome} onGetStarted={onGetStarted} />;
}
